"use client";

import { createContext, useContext, useState, useEffect } from "react";
import { useToast } from "./ToastContext";
import { useAuth } from "./AuthContext"; // Import useAuth
import apiEndpoints from "../apiConfig"; // Import apiEndpoints
import { Favorite, FavoriteBorder } from "@mui/icons-material"; // ❤️ Heart icon

const WishlistContext = createContext(undefined);

export function WishlistProvider({ children }) {
  const [wishlist, setWishlist] = useState([]);
  const { showToast } = useToast();
  const { user } = useAuth(); // Get current user

  // Load wishlist from API
  const loadWishlist = () => {
    if (!user || !user.id) {
      setWishlist([]); // Clear if no user
      return;
    }

    fetch(`${apiEndpoints.getWishlist}?user_id=${user.id}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setWishlist(data.wishlist);
        }
      })
      .catch((err) => console.error("Failed to load wishlist", err));
  };

  // Load on mount and when user changes
  useEffect(() => {
    loadWishlist();
  }, [user]);

  const toggleWishlist = (profile) => {
    if (!user || !user.id) {
      showToast("Please login to save profiles", "error");
      return;
    }

    fetch(apiEndpoints.toggleWishlist, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user_id: user.id,
        profile_user_id: profile.id,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          if (data.action === "added") {
            showToast(
              "Added to wishlist",
              "success",
              <Favorite sx={{ fontSize: 20, color: "#d32f2f" }} />,
            );
          } else {
            showToast(
              "Removed from wishlist",
              "info",
              <FavoriteBorder sx={{ fontSize: 20 }} />,
            );
          }
          loadWishlist(); // Refresh list
        } else {
          showToast(data.message || "Something went wrong", "error");
        }
      })
      .catch((err) => {
        console.error("Error toggling wishlist", err);
        showToast("Network error", "error");
      });
  };

  const addToWishlist = (profile) => toggleWishlist(profile);
  const removeFromWishlist = (profileId) => toggleWishlist({ id: profileId });

  const isInWishlist = (profileId) =>
    wishlist.some((p) => p.id === profileId || p.profile_user_id === profileId);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        wishlistCount: wishlist.length,
        toggleWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context)
    throw new Error("useWishlist must be used within a WishlistProvider");
  return context;
}
